import {
  Alert,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  MultiSelect,
  Radio,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkplace } from '../../../core/access';
import { PageTitle, QueryState, usePositionNames } from '../../../ui/components';
import { useContent, useLayers, useRpc } from '../data';
import {
  companyPositionsOf,
  flagsOf,
  groupChanges,
  reassignPreview,
  vesselPositionsOf,
} from '../model';
import {
  EMPTY_FILTER,
  filterQuestions,
  QuestionFilters,
  type QuestionFilter,
} from './VesselQuestionsPage';

/**
 * D14 Mass reassignment: filter questions → choose them → new position(s) for the company or one
 * vessel (or return to the layer above) → confirmation with counts → one history row per question.
 */
const COMPANY_TARGET = 'company';

export function MassReassignPage() {
  const { t } = useTranslation('dashboard');
  const { company, vessels, access, positions: inUse } = useWorkplace();
  const { name, list } = usePositionNames();
  const content = useContent();
  const layers = useLayers();
  const [target, setTarget] = useState<string>(COMPANY_TARGET);
  const vesselId = target === COMPANY_TARGET ? null : target;
  const [filter, setFilter] = useState<QuestionFilter>(EMPTY_FILTER);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<'set' | 'revert'>('set');
  const [positions, setPositions] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<number | null>(null);
  const order = access.positions.map((p) => p.code);
  const reassign = useRpc('risq_reassign_questions', [
    ['layers', company.id],
    ['history', company.id],
  ]);

  const rows = useMemo(() => {
    if (!content.data || !layers.data) {
      return [];
    }
    const l = layers.data;
    return content.data.questions.map((question) => ({
      question,
      flags: flagsOf(question, l),
      positions:
        vesselId === null
          ? companyPositionsOf(question, l)
          : vesselPositionsOf(question, l, vesselId),
    }));
  }, [content.data, layers.data, vesselId]);
  const shown = useMemo(
    () => filterQuestions(rows, filter, (r) => r.positions.codes),
    [rows, filter],
  );
  const chosen = rows.filter((r) => selected.has(r.question.key)).map((r) => r.question);
  const preview =
    layers.data === undefined
      ? { changes: [], unchanged: 0 }
      : reassignPreview(chosen, layers.data, vesselId, mode === 'revert' ? null : positions, order);
  const allShown = shown.length > 0 && shown.every((r) => selected.has(r.question.key));
  const where =
    vesselId === null
      ? t('editor.wholeCompany')
      : (vessels.find((v) => v.id === vesselId)?.name ?? '');

  const targetOptions = [
    { value: COMPANY_TARGET, label: t('mass.company', { company: company.name }) },
    ...vessels.map((v) => ({ value: v.id, label: t('mass.vessel', { vessel: v.name }) })),
  ];
  const toggleAll = () => {
    const next = new Set(selected);
    for (const r of shown) {
      if (allShown) {
        next.delete(r.question.key);
      } else {
        next.add(r.question.key);
      }
    }
    setSelected(next);
  };
  const toggle = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setSelected(next);
  };

  return (
    <>
      <PageTitle title={t('mass.title')} />
      <QueryState
        loading={content.isPending || layers.isPending}
        error={content.error ?? layers.error}
      >
        {() => (
          <Stack gap="lg">
            <Card withBorder>
              <Stack>
                <Title order={2} size="h3">
                  {t('mass.step1')}
                </Title>
                <Select
                  label={t('mass.where')}
                  data={targetOptions}
                  value={target}
                  allowDeselect={false}
                  onChange={(value) => {
                    setTarget(value ?? 'company');
                    setResult(null);
                  }}
                  maw={420}
                />
              </Stack>
            </Card>
            <Card withBorder>
              <Stack>
                <Title order={2} size="h3">
                  {t('mass.step2')}
                </Title>
                <QuestionFilters
                  filter={filter}
                  onChange={setFilter}
                  sections={content.data?.sections ?? []}
                  withStatus={false}
                />
                <Group>
                  <Text fw={600}>
                    {t('mass.shownSelected', { shown: shown.length, selected: selected.size })}
                  </Text>
                  <Button
                    variant="default"
                    size="xs"
                    onClick={toggleAll}
                    disabled={shown.length === 0}
                  >
                    {allShown ? t('mass.unselectShown') : t('mass.selectShown')}
                  </Button>
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={() => setSelected(new Set())}
                    disabled={selected.size === 0}
                  >
                    {t('mass.clearSelection')}
                  </Button>
                </Group>
                <Table.ScrollContainer minWidth={760} mah={420}>
                  <Table striped verticalSpacing={4} stickyHeader>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t('weekly.choose')}</Table.Th>
                        <Table.Th>{t('questions.code')}</Table.Th>
                        <Table.Th>{t('questions.text')}</Table.Th>
                        <Table.Th>{t('mass.nowHere', { where })}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {shown.map((r) => (
                        <Table.Tr key={r.question.key}>
                          <Table.Td>
                            <Checkbox
                              aria-label={t('weekly.chooseQuestion', { code: r.question.code })}
                              checked={selected.has(r.question.key)}
                              onChange={() => toggle(r.question.key)}
                            />
                          </Table.Td>
                          <Table.Td>{r.question.code}</Table.Td>
                          <Table.Td maw={460}>
                            <Text size="sm" lineClamp={1}>
                              {r.question.textEn}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {list(r.positions.codes, t('questions.unassigned'))}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {t(`layers.source.${r.positions.source}`)}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Stack>
            </Card>
            <Card withBorder>
              <Stack>
                <Title order={2} size="h3">
                  {t('mass.step3')}
                </Title>
                <Radio.Group
                  value={mode}
                  onChange={(value) => setMode(value === 'revert' ? 'revert' : 'set')}
                >
                  <Stack gap="xs">
                    <Radio value="set" label={t('mass.modeSet')} />
                    <Radio
                      value="revert"
                      label={
                        vesselId === null ? t('mass.modeRevertCompany') : t('mass.modeRevertVessel')
                      }
                    />
                  </Stack>
                </Radio.Group>
                {mode === 'set' ? (
                  <MultiSelect
                    label={t('mass.newPositions')}
                    data={inUse.map((p) => ({ value: p.code, label: name(p.code) }))}
                    value={positions}
                    onChange={setPositions}
                    maw={420}
                  />
                ) : null}
                <Text>
                  {t('mass.previewLine', {
                    change: preview.changes.length,
                    unchanged: preview.unchanged,
                  })}
                </Text>
                <Button
                  w="fit-content"
                  disabled={
                    preview.changes.length === 0 || (mode === 'set' && positions.length === 0)
                  }
                  onClick={() => setConfirming(true)}
                >
                  {t('mass.review', { count: preview.changes.length })}
                </Button>
                {result === null ? null : (
                  <Alert color="green" role="status">
                    {t('mass.done', { count: result, where })}
                  </Alert>
                )}
                {reassign.error === null ? null : (
                  <Alert color="red" role="alert">
                    {t('common.saveFailed', { message: reassign.error.message })}
                  </Alert>
                )}
              </Stack>
            </Card>
            <Modal
              opened={confirming}
              onClose={() => setConfirming(false)}
              title={t('mass.confirmTitle', { count: preview.changes.length, where })}
              size="lg"
            >
              <Stack>
                <Text>{t('mass.confirmBody')}</Text>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('history.before')}</Table.Th>
                      <Table.Th>{t('history.after')}</Table.Th>
                      <Table.Th>{t('mass.questions')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {groupChanges(preview.changes).map((g) => (
                      <Table.Tr key={`${g.from.join()}>${g.to.join()}`}>
                        <Table.Td>{list(g.from, t('questions.unassigned'))}</Table.Td>
                        <Table.Td>{list(g.to, t('questions.unassigned'))}</Table.Td>
                        <Table.Td>{g.count}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                <Text size="sm" c="dimmed">
                  {t('mass.codes', { codes: preview.changes.map((c) => c.code).join(', ') })}
                </Text>
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setConfirming(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    loading={reassign.isPending}
                    onClick={() =>
                      reassign.mutate(
                        {
                          p_company_id: company.id,
                          p_vessel_id: vesselId,
                          p_question_keys: preview.changes.map((c) => c.key),
                          p_position_codes: mode === 'revert' ? null : positions,
                        },
                        {
                          onSuccess: (count) => {
                            setResult(count);
                            setConfirming(false);
                            setSelected(new Set());
                          },
                        },
                      )
                    }
                  >
                    {t('mass.confirm', { count: preview.changes.length })}
                  </Button>
                </Group>
              </Stack>
            </Modal>
          </Stack>
        )}
      </QueryState>
    </>
  );
}
